/*
 * ===============================================================================================================
 *                                Copyright 2025, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState } from 'react';
import { ITableWithPaginatorProps } from '../../models/Common/Pagination';
import CustomTablePagination from '../CustomTablePagination';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from './constants';

const TableWithPaginator = <T extends object>({
  data,
  defaultItemsPerPage = DEFAULT_ROWS_PER_PAGE,
  onChangePageOptionsEvent,
  rowsPerPageOptions,
  maxTableBodyHeight,
  totalCount,
  extendedToolbar,
  ...otherProps
}: ITableWithPaginatorProps<T>) => {
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(defaultItemsPerPage);

  const [prevData, setPrevData] = useState(data);
  if (data !== prevData) {
    setPrevData(data);
    setPage(DEFAULT_PAGE);
    setRowsPerPage(defaultItemsPerPage);
  }

  const handleChangePageOptions = (itemsPerPage: number, page: number) => {
    setPage(page);
    setRowsPerPage(itemsPerPage);
    onChangePageOptionsEvent && onChangePageOptionsEvent(page, itemsPerPage);
  };

  return (
    <CustomTablePagination
      paging={false}
      enableColumnActions={false}
      data={data.slice((page - 1) * rowsPerPage, page * rowsPerPage)}
      currentPageNumber={page}
      currentItemsPerPage={rowsPerPage}
      totalCount={totalCount || data.length}
      withoutPagination={data.length <= defaultItemsPerPage}
      maxTableBodyHeight={maxTableBodyHeight}
      onChangePageOptions={handleChangePageOptions}
      rowsPerPageOptions={rowsPerPageOptions || [defaultItemsPerPage, 10, 25]}
      renderTopToolbar={extendedToolbar}
      {...otherProps}
    />
  );
};

export default TableWithPaginator;
